import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Badge,
  useColorModeValue,
  useColorMode,
  Button,
  HStack,
  Td,
  Tr,
} from "@chakra-ui/react";
import { EyeIcon, MailIcon } from "lucide-react";
import { User } from "./BurnoutAnalysisMain";
import { FaFire } from "react-icons/fa";
import { Doughnut, Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";

// Register chart components
ChartJS.register(ArcElement, Tooltip, Legend);

interface CompactUserCardProps {
  user: User;
  onView: (user: User) => void;
  organizationId: string;
}

const CompactUserCard: React.FC<CompactUserCardProps> = ({
  user,
  onView,
  organizationId,
}) => {
  const emailBg = useColorModeValue("gray.50", "gray.800");
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const [validTaskCount, setValidTaskCount] = useState(0);
  const [isValidating, setIsValidating] = useState(true);

  // Validate tasks by checking if they actually exist in Firestore
  useEffect(() => {
    const validateTasks = async () => {
      if (!user.assignedTasks || user.assignedTasks.length === 0) {
        setValidTaskCount(0);
        setIsValidating(false);
        return;
      }

      let validCount = 0;
      const validationPromises = user.assignedTasks.map(async (task: any) => {
        try {
          // Check if task has required properties
          if (
            !task ||
            !task.id ||
            !task.taskId ||
            !task.projectId ||
            !task.channelId
          ) {
            return false;
          }

          // Fetch the task document from Firestore
          const taskDocRef = doc(
            db,
            `organization/${organizationId}/projects/${task.projectId}/channels/${task.channelId}/tasks/${task.taskId}`
          );
          const snapshot = await getDoc(taskDocRef);

          if (snapshot.exists()) {
            const taskData = snapshot.data();
            if (taskData?.data && Array.isArray(taskData.data)) {
              // Recursive function to check if task exists in main tasks or subtasks
              const findTaskInData = (
                tasks: any[],
                targetId: string
              ): boolean => {
                for (const t of tasks) {
                  // Check if this task matches
                  if (t.id === targetId) {
                    return true;
                  }
                  // Check subtasks recursively
                  if (t.subtasks && Array.isArray(t.subtasks)) {
                    if (findTaskInData(t.subtasks, targetId)) {
                      return true;
                    }
                  }
                }
                return false;
              };

              // Check if the user's task actually exists in the task data or its subtasks
              return findTaskInData(taskData.data, task.id);
            }
          }
          return false;
        } catch (error) {
          console.error("Error validating task:", error);
          return false;
        }
      });

      const results = await Promise.all(validationPromises);
      validCount = results.filter(Boolean).length;

      setValidTaskCount(validCount);
      setIsValidating(false);
    };

    validateTasks();
  }, [user.assignedTasks, organizationId]);

  const calculateBurnoutPercentage = (taskCount: number) => {
    if (taskCount === 0) return 0;
    if (taskCount < 3) return 10;
    if (taskCount < 5) return 20;
    if (taskCount <= 10) return 40;
    if (taskCount <= 15) return 60;
    return 80;
  };

  const burnoutPercentage = calculateBurnoutPercentage(validTaskCount);


  let burnoutColor = "green";
  if ( burnoutPercentage >= 70) {
    burnoutColor = "red";
  } else if (burnoutPercentage >= 40) {
    burnoutColor = "green";
  } else if (burnoutPercentage >= 20) {
    burnoutColor = "#F59E0B"; // yellow
  } else {
    burnoutColor = "#4A5568"; // dark gray
  }

  const chartData = {
    datasets: [
      {
        label: "Burnout",
        data: [burnoutPercentage, 100 - burnoutPercentage],
        backgroundColor: [burnoutColor, "#CBD5E0"],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    cutout: "70%",
    plugins: {
      tooltip: { enabled: false },
      legend: { display: false },
    },
  };

  return (
    <Tr _hover={{ bg: isDark ? "#2A3441" : "#F2F4F7" }} transition="all 0.2s">
      <Td>
        <Box position="relative" width="40px" height="40px">
          <Doughnut
            data={chartData}
            options={chartOptions}
            width={40}
            height={40}
          />
          <Text
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            fontSize="10px"
            fontWeight="bold"
            color=  {isDark ? "white" : burnoutColor}
          >
            {isValidating ? "..." : `${burnoutPercentage}%`}
          </Text>
        </Box>
      </Td>
      <Td>
        <HStack spacing={2}>
          {/* avatar placeholder intentionally removed */}
          <Box>
            <Text fontSize="sm">
              {user.firstName} {user.lastName}
            </Text>
            <Flex align="center" gap={1} fontSize="11px" color="gray.600">
              {/* <Icon as={MailIcon} boxSize={3} />
              <Text>{user.email}</Text> */}
            </Flex>
          </Box>
        </HStack>
      </Td>
      <Td>
        <Badge
          color={isDark ? "#FBD282" : "#B59143"}
          bg={isDark ? "transparent" : "transparent"}
          borderColor={isDark ? "#B59143" : "#E8C16C"}
          borderWidth="1px"
          fontSize="0.65rem"
          borderRadius="full"
          px={2}
          py={1}
        >
          {user.role.toUpperCase()}
        </Badge>
      </Td>
      <Td textAlign="center" py={2}>
        <button
          className={
            isDark
              ? "text-xs border border-[#B59143] text-[#FBD282] px-3 py-1 rounded bg-transparent hover:bg-[#1A1C1F] transition"
              : "text-xs border border-[#E8C16C] text-[#B59143] px-3 py-1 rounded bg-transparent hover:bg-[#FAEDD0] transition"
          }
          onClick={() => onView(user)}
        >
          View Details
        </button>
      </Td>
    </Tr>
  );
};

export default CompactUserCard;
